import { useAuth } from '../hooks/useAuth'
import { useEffectAsync } from '../hooks/useEffectAsync'
import { useAtomValue, useSetAtom } from 'jotai'
import { accessTokenAtom, setUserAtom, logoutAtom } from '../store/userAtoms'
import { setupAxiosInterceptor } from '../modules/apiClients'
import { useCallback, useEffect, useRef } from 'react'
import { StandardLoader } from './StandardLoader'

interface AuthInitializerProps {
  children: React.ReactNode
}

export const AuthInitializer: React.FC<AuthInitializerProps> = ({ children }) => {
  const { initializeAuth, isInitializing, clearError } = useAuth()
  const accessToken = useAtomValue(accessTokenAtom)
  const setUser = useSetAtom(setUserAtom)
  const setAccessTokenAtom = useSetAtom(accessTokenAtom)
  const logout = useSetAtom(logoutAtom)
  const accessTokenRef = useRef<string | null>(accessToken)

  useEffect(() => {
    accessTokenRef.current = accessToken
  }, [accessToken])

  const updateAccessToken = useCallback(
    (token: string | null) => {
      accessTokenRef.current = token
      setAccessTokenAtom(token)
    },
    [setAccessTokenAtom]
  )

  const handleLogout = useCallback(() => {
    accessTokenRef.current = null
    logout()
  }, [logout])

  // Set up axios interceptors with automatic session refresh capability 🏴‍☠️⭐
  useEffect(() => {
    const cleanupInterceptors = setupAxiosInterceptor(
      () => accessTokenRef.current,     // Get current access token
      updateAccessToken,        // Update access token after refresh
      setUser,              // Update user data after refresh
      handleLogout,               // Logout if refresh fails
      clearError            // Clear any lingering auth errors after refresh
    )
    return cleanupInterceptors
  }, [updateAccessToken, setUser, handleLogout, clearError])

  useEffectAsync(initializeAuth, [initializeAuth])

  // Show loading state while initializing auth
  if (isInitializing) {
    return <StandardLoader text="Initializing..." fullScreen />
  }
  return <>{children}</>
}
